<script lang="ts" setup>
import Loading from '@/components/common/Loading.vue';
import { useLoginManager } from '@/login/composables/useLoginManager';
import { useLoginStore } from '@/login/stores/useLoginStore';
import { storeToRefs } from 'pinia';
import { reactive } from 'vue';
import ForgotPassword from '@/views/newlogin/components/ForgotPassword.vue';

const { loginWithPassword, loginWithSmsCode, requireSms } = useLoginManager();
const loginStore = useLoginStore();
const { saveUsername, savedUsername, passwordLoginError, smsLoginError, forgotPassword, loggingIn } = storeToRefs(loginStore);

const passwordLogin = reactive({
    username: savedUsername.value ?? '',
    password: '',
    smsCode: ''
});

function reset(): void {
    passwordLogin.username = '';
    passwordLogin.password = '';
    passwordLogin.smsCode = '';
}

async function login(): Promise<void> {
    const result = await loginWithPassword(passwordLogin.username, passwordLogin.password);

    if (result === false) {
        return;
    }

    if (saveUsername.value) {
        loginStore.setSavedUsername(passwordLogin.username);
    }

    if (!requireSms.value) {
        reset();
    }
}

async function sms  (): Promise<void> {
    const result = await loginWithSmsCode(passwordLogin.username, passwordLogin.smsCode);

    if (result === false) {
        return;
    }

    reset();
}
</script>

<template>
    <ForgotPassword v-if="forgotPassword" :preset-username="passwordLogin.username" />

    <Loading
        v-else
        :value="loggingIn"
        :use-overlay="false"
        size="xxxlarge"
    >
        <form @submit.prevent>
            <SText
                is="p"
                size="2"
                class="mb-8"
            >
                {{ $t('components.login.welcomeBack') }}
            </SText>
            <template v-if="requireSms">
                <SFormGroup id="field" :label="$t('components.login.smsCode.title')" class="mb-7">
                    <SFormInput v-model="passwordLogin.smsCode" type="text" />
                </SFormGroup>
                <SStack vertical>
                    <SButton
                        variant="primary"
                        block
                        @click="sms"
                    >
                        {{ $t('components.login.login') }}
                    </SButton>
                </SStack>
            </template>
            <template v-else>
                <SFormGroup id="field" :label="$t('system.username')" class="mb-7">
                    <SFormInput v-model="passwordLogin.username" type="text" />
                </SFormGroup>

                <SFormPassword
                    v-model="passwordLogin.password"
                    :label="$t('system.password')"
                    :caps-lock-warning="$t('components.login.capsLockWarning')"
                    class="mb-7"
                />

                <SStack class="mb-7" align-items="center" justify-content="between">
                    <SFormCheckbox
                        id="remember-me"
                        v-model="saveUsername"
                        :label="$t('system.rememberMe')"
                    />
                    <a class="link-primary fs-4" @click="loginStore.setForgotPassword(true)">{{ $t('components.login.forgotYourPassword') }}</a>
                </SStack>
                <SAlert
                    v-if="passwordLoginError"
                    :dismissible="true"
                    variant="danger"
                    class="mb-6"
                >
                    {{ $t('errors.errorCode.6') }}
                </SAlert>
                <SAlert
                    v-if="smsLoginError"
                    :dismissible="true"
                    variant="danger"
                    class="mb-6"
                >
                    {{ $t('errors.errorCode.19') }}
                </SAlert>
                <SStack :vertical="true" class="mt-2">
                    <SButton
                        variant="primary"
                        class="w-100"
                        @click="login"
                    >
                        {{ $t('components.login.login') }}
                    </SButton>
                </SStack>
            </template>
        </form>
    </Loading>
</template>
